import React, { useState, useEffect } from 'react';
import { useChat } from '../hooks/useChat';
import { chatService } from '../../../shared/services/chatService';
import { useChatStore } from '../../../shared/store/chatStore';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { ShareChatDialog } from './ShareChatDialog';
import { InfoSidebar } from '../../../shared/components/layout/InfoSidebar';
import { useBibleStore } from '../../../shared/store/bibleStore';
import { useParams } from 'react-router-dom';
import { Header } from '../../../shared/components/layout/Header';
import { WebSocketService } from '../../../shared/services/websocketService';

export default function Chat() {
    const { id } = useParams();
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const { isInfoSidebarOpen, selectedVerse, selectedVerseText } = useBibleStore();
    const { messages, isLoading, streaming, currentChat, actions } = useChat();
    const isMessageLoading = useChatStore(state => state.isMessageLoading);

    const handleSendMessage = (message: string) => {
        if (!currentChat?.thread_id || !id) return;
        actions.sendMessage(message);
    };

    useEffect(() => {
        window.openShareDialog = () => setIsShareDialogOpen(true);
        return () => {
            delete window.openShareDialog;
        };
    }, []);

    useEffect(() => {
        const pendingMessage = sessionStorage.getItem('pendingMessage');
        if (pendingMessage && currentChat) {
            // Send the pending message
            actions.sendMessage(pendingMessage);
            // Clear pending message
            sessionStorage.removeItem('pendingMessage');
        }
    }, [currentChat]); // Only run when currentChat is loaded

    // Set current chat when mounting/unmounting
    useEffect(() => {
        if (!currentChat?.id) return;

        // Set current chat when mounting
        useChatStore.setState({ currentChat });

        // Clear chat state when unmounting
        return () => {
            useChatStore.setState({
                currentChat: null,
                streaming: {
                    message: null,
                    questions: null,
                    verses: null,
                    error: false
                }
            });
        };
    }, [currentChat?.id]);

    return (
        <div className="flex flex-col h-screen">
            <ShareChatDialog
                isOpen={isShareDialogOpen}
                onClose={() => setIsShareDialogOpen(false)}
                chatId={id || ''}
                isPublic={currentChat?.is_public || false}
            />

            <Header />

            <main className="flex-1 overflow-y-auto pt-[60px] pb-24 relative" style={{ zIndex: 1 }}>
                <div className="max-w-2xl mx-auto">
                    <MessageList
                        messages={messages}
                        isLoading={isLoading}
                        isGeneratingQuestions={streaming.questions !== null}
                        streamingMessage={streaming.message}
                        streamingQuestions={streaming.questions}
                        streamingError={streaming.error}
                        isMessageLoading={isMessageLoading}
                        onTellMeMore={(verse) => {
                            if (currentChat?.thread_id && id) {
                                actions.tellMeMore(verse, currentChat.thread_id, id);
                            }
                        }}
                        onFollowUpQuestion={(question) => {
                            if (currentChat?.thread_id && id) {
                                actions.followUpQuestion(question, currentChat.thread_id, id);
                            }
                        }}
                        onRetry={() => {
                            if (currentChat?.thread_id && id) {
                                actions.retryLastMessage(currentChat.thread_id, id);
                            }
                        }}
                        chatId={id || ''}
                        threadId={currentChat?.thread_id || ''}
                    />
                </div>
            </main>

            <div className={`fixed bottom-0 left-0 transition-all duration-300 bg-white
                ${isInfoSidebarOpen ? 'right-[320px]' : 'right-0'}`} style={{ zIndex: 50 }}>
                <ChatInput
                    value={inputValue}
                    onChange={setInputValue}
                    onSendMessage={handleSendMessage}
                    isLoading={isLoading}
                />
            </div>

            <InfoSidebar
                isOpen={isInfoSidebarOpen}
                onClose={() => useBibleStore.setState({ isInfoSidebarOpen: false })}
                verse={selectedVerse || undefined}
                verseText={selectedVerseText || undefined}
            />
        </div>
    );
}